import {
  Component,
  Inject,
  Input,
  OnChanges,
  signal,
  WritableSignal,
  OnInit,
} from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, DOCUMENT, NgOptimizedImage } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { StationInfoComponent } from '../../components/station-details/station-info/station-info.component';
import { StationService } from '@app/services/station.service';
import { Station } from '@app/services/station.interface';
import { prettyAddress } from '@app/shared/utils';
import { MatDividerModule } from '@angular/material/divider';
import { ImageService } from '@app/shared/services/image.service';
import { SkeletonComponent } from '@app/components/skeleton/skeleton.component';
import { ConnectorsTabComponent } from '@app/components/station-details/connectors-tab/connectors-tab.component';
import { ImagesTabComponent } from '@app/components/station-details/images-tab/images-tab.component';
import { RatingsTabComponent } from '@app/components/station-details/ratings-tab/ratings-tab.component';
import { Router, RouterOutlet } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GcImageComponent } from '@app/components/gc-image/gc-image.component';
import { firstValueFrom } from 'rxjs';
import { SimplebarAngularModule } from 'simplebar-angular';
import { AuthService, UserSignal } from '@app/services/auth.service';
import { getMarkerUrl } from '@app/shared/utils/station';
import { ReportDialogComponent } from '@app/components/report-dialog/report-dialog.component';
import { ClaimChargerDialogComponent } from '@app/components/claim-charger-dialog/claim-charger-dialog.component';
import { EditChargerDialogComponent } from '@app/components/edit-charger-dialog/edit-charger-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CategoryType } from '@app/services/feedback.interface';
import { StationDetailsService } from './station-details.service';
import { SearchControlService } from '../map/controls/services/search-control.service';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { SafeUrlPipe } from '@app/shared/pipes/safe-url.pipe';
import { DevcycleService, FEATURE_FLAGS } from '@app/services/devcycle.service';
import { SeoService } from '@app/services/seo.service';
import { AdStripComponent } from '@app/shared/components/ad-strip/ad-strip.component';
import { AdNativeComponent } from '@app/shared/components/ad-native/ad-native.component';
import { AdBannerComponent } from '@app/shared/components/ad-banner/ad-banner.component';
import { StationChatFabComponent } from '@app/features/chat/components/station-chat-fab/station-chat-fab.component';

@Component({
  selector: 'app-station-details',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    NgOptimizedImage,
    StationInfoComponent,
    MatDividerModule,
    SkeletonComponent,
    ConnectorsTabComponent,
    ImagesTabComponent,
    RatingsTabComponent,
    RouterOutlet,
    GcImageComponent,
    SimplebarAngularModule,
    TourAnchorMatMenuDirective,
    SafeUrlPipe,
    AdStripComponent,
    AdNativeComponent,
    AdBannerComponent,
    StationChatFabComponent,
  ],
  templateUrl: './station-details.component.html',
  styleUrl: './station-details.component.scss',
})
export class StationDetailsComponent implements OnInit, OnChanges {
  @Input() stationId = '';
  station: Station = null;
  loadingStates: WritableSignal<{
    station?: boolean;
    image?: boolean;
    ratings?: boolean;
  }> = signal({
    station: false,
    image: false,
    ratings: false,
  });
  fullAddress = '';
  stationImage = null;
  ratings: { rating: number; count: number } = null;
  isBookmarked = signal(false);
  user = signal<UserSignal>(null);
  markerUrl = '';
  showEditCharger = signal(false);
  showRepairReport = signal(false);

  stationNotFound = false;

  constructor(
    private snackBar: MatSnackBar,
    private stationService: StationService,
    private imageService: ImageService,
    public authService: AuthService,
    private dialog: MatDialog,
    private router: Router,
    private stationDetailsService: StationDetailsService,
    private searchControlService: SearchControlService,
    @Inject(DOCUMENT) private document: Document,
    private devcycleService: DevcycleService,
    private seoService: SeoService,
  ) {
    this.user = this.authService.userSignal;
  }

  async ngOnInit() {
    this.showEditCharger.set(this.devcycleService.getFeatureFlag(FEATURE_FLAGS.EDIT_CHARGER_ENABLE, false));
    this.devcycleService.onVariableUpdated(FEATURE_FLAGS.EDIT_CHARGER_ENABLE, (key, value) => {
      this.showEditCharger.set(value);
    });
    this.showRepairReport.set(this.devcycleService.getFeatureFlag(FEATURE_FLAGS.REPAIR_REPORT_ENABLE, false));
    this.devcycleService.onVariableUpdated(FEATURE_FLAGS.REPAIR_REPORT_ENABLE, (key, value) => {
      this.showRepairReport.set(value);
    });
  }

  async ngOnChanges(): Promise<void> {
    this.station = null;
    this.stationImage = null;
    this.fullAddress = '';
    this.ratings = null;

    this.loadingStates.update((prev) => ({
      ...prev,
      station: true,
    }));
    this.station = await firstValueFrom(
      this.stationService.getStationById([this.stationId]),
    ).then((station) => station?.data?.stationById[0] ?? null);
    this.loadingStates.update((prev) => ({
      ...prev,
      station: false,
    }));

    if (!this.station) {
      return;
    }

    this.markerUrl = getMarkerUrl(this.station.chargers);
    let cityAndState = this.station?.locationCity && this.station?.locationState ? this.station?.locationCity + ', ' + this.station?.locationState : (this.station?.locationCity ?? '') + (this.station?.locationState ?? '');
    const locationLabel = cityAndState || 'EV';
    this.seoService.update({
      title: `${this.station.name} | ${locationLabel} Charging Station | GitCharger`,
      description: `Find charging details, connector availability, ratings, and directions for ${this.station.name}${cityAndState ? ' in ' + cityAndState : ''}. Plan your EV charging stop with GitCharger.`,
      url: `https://gitcharger.com/station/${this.stationId}`,
    });

    this.fullAddress = prettyAddress(
      this.station?.locationAddress,
      this.station?.locationAddress2,
      this.station?.locationCity,
      this.station?.locationState,
    );
    this.isBookmarked.set(this.station?.isBookmarked ?? false);

    this.loadingStates.update((prev) => ({
      ...prev,
      ratings: true,
    }));
    await this.getRatings().finally(() => {
      this.loadingStates.update((prev) => ({
        ...prev,
        ratings: false,
      }));
    });

    this.loadingStates.update((prev) => ({
      ...prev,
      image: true,
    }));
    this.imageService
      .getImage(
        this.stationService.getStationImageUrl(this.stationId)
      )
      .subscribe({
        next: (src: string) => {
          this.stationImage = src;
          this.loadingStates.update((prev) => ({
            ...prev,
            image: false,
          }));
        },
        error: () => {
          this.stationImage = null;
          this.loadingStates.update((prev) => ({
            ...prev,
            image: false,
          }));
        },
      });
  }

  async getRatings() {
    this.ratings = await this.stationDetailsService.getRatings(this.stationId);
  }

  share() {
    const shareLink = this.document.location.origin + '/station/' + this.stationId;
    navigator.clipboard.writeText(shareLink);
  }

  async toggleBookmark() {
    if (!this.authService.isAuthenticated()) return;

    if (this.isBookmarked()) {
      // already bookmarked
      this.isBookmarked.set(false);
      this.stationService.deleteBookmark(this.stationId).subscribe();
    } else {
      const bookmarks = await firstValueFrom(this.stationService.getBookmarks(this.user().uid));
      if (bookmarks.length >= 10) {
        this.snackBar.open('You have reached the maximum number of bookmarked stations', undefined, { duration: 3000 });
        return;
      }

      this.isBookmarked.set(true);
      this.stationService.addBookmark(this.stationId, this.user().uid).subscribe();
    }
  }

  openReportDialog() {
    this.dialog.open(ReportDialogComponent, {
      width: '500px',
      data: {
        categoryType: CategoryType.STATION,
        categoryId: this.stationId,
      },
    });
  }

  openClaimDialog() {
    this.dialog.open(ClaimChargerDialogComponent, {
      width: '500px',
      data: {
        stationId: Number(this.stationId),
        stationName: this.station?.name,
      },
    });
  }

  goBack() {
    this.router.navigate(['/']);
  }

  fillNavigation() {
    this.searchControlService.autoFillDestination.set(this.fullAddress);
    this.router.navigate([`/navigation`]);
  }

  openEditDialog() {
    const dialogRef = this.dialog.open(EditChargerDialogComponent, {
      maxWidth: '560px',
      width: '560px',
      maxHeight: '90vh',
      data: { station: this.station },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated) {
        // Refresh station data
        this.ngOnChanges();
      }
    });
  }
}
